/**
 * How people refer to agents. Stored handles are unique team-wide, so the dashboard
 * prefixes them with the owner ("alice-reviewer"); people should never have to read or
 * type that prefix twice. These helpers split an agent into the person and the name:
 *
 *   owner alice, general agent          → display "general", short "general", address "@alice/general"
 *   owner alice, handle alice-reviewer  → display "reviewer", short "reviewer", address "@alice/reviewer"
 *   owner alice, handle qa-bot          → display "qa-bot",   short "qa-bot",   address "@alice/qa-bot"
 *
 * The address form is what `baton_send` already resolves as owner/role, so what is
 * printed is also what can be typed.
 */
/** The name without the owner prefix; a general agent is simply "general". */
export function agentDisplayName(agent, ownerHandle) {
    return agentShortName(agent, ownerHandle);
}
/** The name alone, never the owner: "general" for a general agent, else the handle minus the owner prefix. */
export function agentShortName(agent, ownerHandle) {
    if (agent.role === null)
        return "general";
    if (ownerHandle) {
        const prefix = `${ownerHandle.toLowerCase()}-`;
        if (agent.handle.toLowerCase().startsWith(prefix) && agent.handle.length > prefix.length) {
            return agent.handle.slice(prefix.length);
        }
    }
    return agent.handle;
}
/** Owner and name, separately: "@alice/general" for a general agent, "@alice/reviewer" otherwise; both resolve as send addresses. */
export function agentAddress(agent, ownerHandle) {
    const name = agentShortName(agent, ownerHandle);
    if (!ownerHandle)
        return agent.role === null ? agent.handle : name;
    return `@${ownerHandle}/${name}`;
}
/** The stored handle for a new agent named `name` by `ownerHandle`; general agents use the owner's handle. */
export function agentHandleFor(ownerHandle, name) {
    const slug = name.trim().toLowerCase();
    if (!slug)
        return ownerHandle;
    return slug.startsWith(`${ownerHandle.toLowerCase()}-`) ? slug : `${ownerHandle}-${slug}`;
}
//# sourceMappingURL=naming.js.map